import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ApolloError, gql, useMutation} from '@apollo/client';
import {EzButton, EzModal} from '@ezcater/recipe';
import {useFlashMessages} from '@ezcater/flash-messages';
import type {
  DeliveryManagementUnassignDriverMutation,
  DeliveryManagementUnassignDriverMutationVariables,
} from '../../__generated__/DeliveryManagementUnassignDriverMutation';
import formattedOrderNumber from '../../util/formattedOrderNumber';

type UnassignDriverProps = {
  orderNumber: string;
  deliveryId: string;
  driverId: string;
  onSubmitCompleted?: (payload: DeliveryManagementUnassignDriverMutation['unassignDriver']) => void;
  onSubmitError?: (error: ApolloError) => void;
};

const UnassignDriver = ({
  orderNumber,
  deliveryId,
  driverId,
  onSubmitCompleted,
  onSubmitError,
}: UnassignDriverProps) => {
  const [t] = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unassignDriver] = useMutation<
    DeliveryManagementUnassignDriverMutation,
    DeliveryManagementUnassignDriverMutationVariables
  >(UnassignDriverMutation);
  const {addFlashMessage} = useFlashMessages();

  const handleButtonClick = () => {
    setIsModalOpen(true);
  };

  const handleModalDismiss = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      const unassignDriverPayload = await unassignDriver({
        variables: {
          input: {
            deliveryId,
            driverId,
          },
        },
      });

      handleModalDismiss();

      addFlashMessage({
        type: 'success',
        message: t('components.deliveryManagement.unassignDriver.flashMessages.submitSuccess', {
          orderNumber: formattedOrderNumber(orderNumber),
        }),
      });

      if (onSubmitCompleted) {
        await onSubmitCompleted(unassignDriverPayload?.data?.unassignDriver || null);
      }
    } catch (error) {
      handleModalDismiss();

      addFlashMessage({
        type: 'error',
        message: t('components.deliveryManagement.unassignDriver.flashMessages.submitError', {
          orderNumber: formattedOrderNumber(orderNumber),
        }),
      });

      if (onSubmitError && error instanceof ApolloError) {
        await onSubmitError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <EzButton legacy use="tertiary" onClick={handleButtonClick}>
        {t('components.deliveryManagement.unassignDriver.modalToggler')}
      </EzButton>
      <EzModal
        isOpen={isModalOpen}
        onSubmit={handleSubmit}
        submitLabel={t('components.deliveryManagement.unassignDriver.modalSubmitLabel')}
        dismissLabel={t('components.deliveryManagement.unassignDriver.modalDismissLabel')}
        headerText={t('components.deliveryManagement.unassignDriver.modalHeaderText')}
        isSubmitting={isSubmitting}
        onDismiss={handleModalDismiss}
      >
        <p>{t('components.deliveryManagement.unassignDriver.modalBody')}</p>
      </EzModal>
    </div>
  );
};

export const UnassignDriverMutation = gql`
  mutation DeliveryManagementUnassignDriverMutation($input: UnassignDriverInput!) {
    unassignDriver(input: $input) {
      delivery {
        id
        status
        driver {
          id
        }
      }
      errors
    }
  }
`;

export default UnassignDriver;
